const StorageRegistry = new Map();

export class AppStorage<
  TInput extends string | Record<string | number, unknown>,
> {
  #key: string;
  #dataType: 'json' | 'text';
  #storage: Storage;

  constructor(
    key: string,
    dataType: 'json' | 'text' = 'json',
    storageType: 'local' | 'session',
  ) {
    // test is localstorage working or not, if not. use the polyfill memory storage (just pure object in closure)
    if (storageType === 'local') {
      this.#storage = window.localStorage;
    } else if (storageType === 'session') {
      this.#storage = window.sessionStorage;
    } else {
      this.#storage = this.#createStoragePolyfill();
    }

    this.#checkStorageAvailable();
    this.#registerStorage(key);
    this.#key = key;
    this.#dataType = dataType;
  }

  #checkStorageAvailable = () => {
    try {
      const TEST_KEY = '__TEST__';
      this.#storage.setItem(TEST_KEY, '');
      this.#storage.removeItem(TEST_KEY);
    } catch {
      this.#storage = this.#createStoragePolyfill();
    }
  };

  #registerStorage = (key: string) => {
    if (StorageRegistry.has(key)) {
      throw new Error('duplicated Key');
    }

    StorageRegistry.set(key, this);
  };

  #createStoragePolyfill = () => {
    const obj = {} as Record<string, string>;
    return {
      getItem: (key) => obj?.[key],
      setItem: (key, item) => {
        obj[key] = item;
      },
    } as Storage;
  };

  #get = (): TInput | null => {
    if (!this.#storage) throw new Error();
    try {
      const text = this.#storage.getItem(this.#key);

      if (text === null) return null;

      if (this.#dataType === 'text') {
        return text as TInput;
      }

      return JSON.parse(text ?? '');
    } catch {
      console.trace();
      return null;
    }
  };

  #set = (input: TInput) => {
    if (!this.#storage) throw new Error();
    let text: string;

    if (this.#dataType === 'text') {
      text = input as string;
      this.#storage.setItem(this.#key, text);
      return;
    }
    if (this.#dataType === 'json') {
      text = JSON.stringify(input);
      this.#storage.setItem(this.#key, text);
      return;
    }
  };

  set(input: TInput) {
    this.#set(input);
  }

  get() {
    return this.#get();
  }
}
